import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Menu, UnstyledButton, Group, Text, Loader, Badge } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBuilding, IconChevronDown, IconCheck } from '@tabler/icons-react';
import { getCustomerMe, switchPortalCompany } from '../../lib/api.customer';
import { useCustomerStore } from '../../stores/customerStore';
import { supportsAllCompaniesView } from './deadlines/useDeadlineScope';

/**
 * Umschalter zwischen den Gesellschaften eines Konzerns — oben rechts im Kopf, die EINE Stelle dafür.
 *
 * Erscheint nur, wenn dieser Ansprechpartner mehr als eine sehen darf — bei allen
 * anderen wäre er ein Bedienelement ohne Funktion.
 *
 * Auf Seiten, die Posten mehrerer Gesellschaften zusammen zeigen können (Verträge & Lizenzen,
 * Dashboard), steht zusätzlich „Alle Gesellschaften" — und ist dort der Standard. Die Auswahl ändert
 * dann nur die Ansicht; die aktive Gesellschaft (für Eingaben wie eigene Verträge) bleibt.
 *
 * Nach dem Wechsel wird `/me` neu geladen und der Query-Cache verworfen: Bereiche,
 * Rechte und sämtliche Listen hängen an der Gesellschaft. Ohne das Leeren stünden
 * für einen Moment die Daten der vorigen Firma unter dem neuen Namen — die eine
 * Verwechslung, die man in einem Konzernportal nicht haben darf.
 */
export function PortalCompanySwitcher() {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const contact = useCustomerStore((s) => s.contact);
  const setContact = useCustomerStore((s) => s.setContact);
  const allCompaniesView = useCustomerStore((s) => s.allCompaniesView);
  const setAllCompaniesView = useCustomerStore((s) => s.setAllCompaniesView);
  const [switching, setSwitching] = useState<string | null>(null);

  const companies = contact?.companies ?? [];
  if (companies.length < 2) return null;

  const active = companies.find((c) => c.id === contact?.activeCustomerId) ?? companies[0];
  const offersAll = supportsAllCompaniesView(pathname);
  const showingAll = offersAll && allCompaniesView;

  async function handleSwitch(customerId: string) {
    if (switching) return;
    // Dieselbe Gesellschaft aus der Gesamtsicht heraus: nur die Ansicht einengen, kein Wechsel nötig.
    if (customerId === active.id) {
      setAllCompaniesView(false);
      return;
    }
    setSwitching(customerId);
    try {
      await switchPortalCompany(customerId);
      setAllCompaniesView(false);
      queryClient.clear();
      setContact(await getCustomerMe());
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Wechsel fehlgeschlagen.',
        color: 'red',
      });
    } finally {
      setSwitching(null);
    }
  }

  const checkSlot = (checked: boolean) => (checked ? <IconCheck size={14} /> : <span style={{ width: 14 }} />);

  return (
    <Menu shadow="md" width={280} position="bottom-end">
      <Menu.Target>
        <UnstyledButton
          aria-label="Gesellschaft wechseln"
          style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 8,
            padding: '4px 10px',
          }}
        >
          <Group gap={6} wrap="nowrap">
            <IconBuilding size={16} />
            <Text size="sm" fw={500} lineClamp={1} maw={180}>
              {showingAll ? 'Alle Gesellschaften' : active.name}
            </Text>
            {switching ? <Loader size={12} /> : <IconChevronDown size={14} />}
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Gesellschaft</Menu.Label>
        {offersAll && (
          <>
            <Menu.Item onClick={() => setAllCompaniesView(true)} leftSection={checkSlot(showingAll)}>
              <Text size="sm">Alle Gesellschaften</Text>
              <Text size="xs" c="dimmed">Alle Posten zusammen, mit Kennzeichen</Text>
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
        {companies.map((company) => (
          <Menu.Item
            key={company.id}
            onClick={() => void handleSwitch(company.id)}
            leftSection={checkSlot(!showingAll && company.id === active.id)}
            rightSection={
              company.isHome ? (
                <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                  eigene
                </Badge>
              ) : undefined
            }
          >
            <Text size="sm" lineClamp={1}>{company.name}</Text>
            {company.customerNumber && (
              <Text size="xs" c="dimmed">{company.customerNumber}</Text>
            )}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
